import { useRef, useState, useEffect } from 'react';

const SCANNER_SPECIAL_CHARACTER = '~';

const isContentEditable = (element: HTMLElement) => {
  if (typeof element.getAttribute !== 'function') {
    return false;
  }
  return !!element.getAttribute('contenteditable');
};

const isInput = (element: HTMLElement | null) => {
  if (!element) {
    return false;
  }
  const { tagName } = element;
  const editable = isContentEditable(element);
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || editable;
};

const onKeyDetectDefault = (e: KeyboardEvent) => {
  if (e.key === SCANNER_SPECIAL_CHARACTER) {
    (document.activeElement as HTMLElement).blur();
  }
};

export type ScannerProps = {
  onScanButtonLongPressed?: (data: string, numPresses: number) => void;
  scanButtonLongPressThreshold?: number;
  onError?: (data: string, error: string) => void;
  onKeyDetect?: (e: KeyboardEvent) => void;
  onReceive?: (e: KeyboardEvent) => void;
  minLength?: number;
  avgTimeByChar?: number;
  timeBeforeScanTest?: number;
  endChar?: string[];
  startChar?: string[];
  scanButtonKeyCode?: string;
  stopPropagation?: boolean;
  preventDefault?: boolean;
};

export const useScanner = ({
  onScanButtonLongPressed,
  onError,
  onKeyDetect = onKeyDetectDefault,
  onReceive,
  minLength = 2,
  avgTimeByChar = 30,
  timeBeforeScanTest = 100,
  endChar = [SCANNER_SPECIAL_CHARACTER],
  startChar = [SCANNER_SPECIAL_CHARACTER],
  scanButtonKeyCode,
  scanButtonLongPressThreshold = 3,
  stopPropagation,
  preventDefault,
}: ScannerProps) => {
  const firstCharTime = useRef<number>(0);
  const lastCharTime = useRef<number>(0);
  const stringWriting = useRef<string>('');
  const callIsScanner = useRef<boolean>(false);
  const testTimer = useRef<NodeJS.Timeout | null>(null);
  const scanButtonCounter = useRef<number>(0);
  const [scannedString, setScannedString] = useState<string>('');

  const resetScannedString = () => setScannedString('');

  const setScannedStringHandler = (newString: string) => {
    setScannedString(newString);
  };

  const inIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    const { target } = e;

    if (target instanceof window.HTMLElement && isInput(target)) {
      return;
    }

    if (scanButtonKeyCode && e.key === scanButtonKeyCode) {
      scanButtonCounter.current += 1;
      e.preventDefault();
      e.stopImmediatePropagation();
    }

    if (onKeyDetect) onKeyDetect(e);

    if (stopPropagation) e.stopImmediatePropagation();
    if (preventDefault) e.preventDefault();

    if (firstCharTime.current && endChar.indexOf(e.key) !== -1) {
      e.preventDefault();
      e.stopImmediatePropagation();
      callIsScanner.current = true;
    } else if (!firstCharTime.current && startChar.indexOf(e.key) !== -1) {
      e.preventDefault();
      e.stopImmediatePropagation();
      callIsScanner.current = false;
    } else {
      if (typeof e.key !== 'undefined') {
        stringWriting.current += e.key;
      }
      callIsScanner.current = false;
    }

    if (!firstCharTime.current) {
      firstCharTime.current = Date.now();
    }
    lastCharTime.current = Date.now();

    if (testTimer.current) clearTimeout(testTimer.current);
    if (callIsScanner.current) {
      scannerDetectionTest();
      testTimer.current = null;
    } else {
      testTimer.current = setTimeout(scannerDetectionTest, timeBeforeScanTest);
    }

    if (onReceive) onReceive(e);
  };

  useEffect(() => {
    const targetDocument = inIframe()
      ? window.parent.document
      : window.document;
    targetDocument.addEventListener('keypress', handleKeyPress);

    return () => {
      targetDocument.removeEventListener('keypress', handleKeyPress);
    };
  }, []);

  const initScannerDetection = () => {
    firstCharTime.current = 0;
    stringWriting.current = '';
    scanButtonCounter.current = 0;
  };

  const scannerDetectionTest = () => {
    if (!scanButtonCounter.current) {
      scanButtonCounter.current = 1;
    }

    if (
      stringWriting.current.length >= minLength &&
      lastCharTime.current - firstCharTime.current <
        stringWriting.current.length * avgTimeByChar
    ) {
      if (
        onScanButtonLongPressed &&
        scanButtonCounter.current > scanButtonLongPressThreshold
      ) {
        onScanButtonLongPressed(
          stringWriting.current,
          scanButtonCounter.current
        );
      } else {
        setScannedString(stringWriting.current);
      }
      initScannerDetection();
      return true;
    }

    let errorMsg = '';
    if (stringWriting.current.length < minLength) {
      errorMsg = `String length should be greater or equal ${minLength}`;
    } else if (
      lastCharTime.current - firstCharTime.current >
      stringWriting.current.length * avgTimeByChar
    ) {
      errorMsg = `Average key character time should be less or equal ${avgTimeByChar}ms`;
    }
    if (onError) onError(stringWriting.current, errorMsg);
    initScannerDetection();
    return false;
  };

  return {
    scannedString,
    setScannedStringHandler,
    resetScannedString,
  };
};
